import React, {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import {
  getPaymentScheduleType,
  PaymentTypeChange,
  PolicyChangeType,
  Quote,
} from '../../domain'
import { PaymentScheduleType } from '../../state/configuration'

interface PaymentScheduleObj {
  paymentScheduleType: PaymentScheduleType
  setPaymentScheduleType: (type: PaymentScheduleType) => void
  getScheduleChanges: () => PaymentTypeChange[]
  isPaymentScheduleDirty: boolean
}

const PaymentScheduleContext = createContext<PaymentScheduleObj | undefined>(undefined)

export interface PaymentScheduleProviderProps extends PropsWithChildren {
  quote: Quote
}

export const PaymentScheduleProvider: FunctionComponent<PaymentScheduleProviderProps> = ({
  quote,
  children,
}) => {
  const quoteScheduleType = getPaymentScheduleType(quote)

  const [paymentScheduleType, setPaymentScheduleType] = useState(quoteScheduleType)

  const isPaymentScheduleDirty = quoteScheduleType !== paymentScheduleType

  useEffect(() => {
    if (isPaymentScheduleDirty) {
      setPaymentScheduleType(quoteScheduleType)
    }
  }, [quote])

  const getScheduleChanges = useCallback<PaymentScheduleObj['getScheduleChanges']>(
    () =>
      isPaymentScheduleDirty
        ? [
            {
              changeType: PolicyChangeType.SELECT_PAYMENT_SCHEDULE,
              paymentScheduleType,
            },
          ]
        : [],
    [paymentScheduleType, quote],
  )

  return (
    <PaymentScheduleContext.Provider
      value={{
        paymentScheduleType,
        setPaymentScheduleType,
        getScheduleChanges,
        isPaymentScheduleDirty,
      }}
    >
      {children}
    </PaymentScheduleContext.Provider>
  )
}

export const usePaymentSchedule = (): PaymentScheduleObj => {
  const context = useContext(PaymentScheduleContext)
  if (!context) {
    throw new Error('Cannot use PaymentScheduleContext when undefined.')
  }
  return context
}
